export const MILES_PER_KM = 0.621371;
export const FEET_PER_MILE = 5280;
export const FEET_PER_METER = 3.28084;
export const METERS_PER_FOOT = 1 / FEET_PER_METER;
export const FEET_PER_KM = FEET_PER_MILE * MILES_PER_KM;
export const MM_PER_INCH = 25.4;
export const MPS_TO_MPH = 2.23694;
export const NM_TO_MILES = 1.15078;
export const MILES_TO_NM = 1 / NM_TO_MILES;

export const SQ_M_PER_ACRE = 4046.872;

export const REORDER_TOLERANCE_METERS = 40;

export const EPOCH = 1609459200; // number of seconds from the Unix Epoch to 1/1/2021 00:00:00 GMT

// the letter 'r' is not included in the character set so we can easily
// differentiate box IDs from Airtable record IDs (all start with 'rec')
// this is used for a base32 encoding so we need 2^5 = 32 characters
// with upper case, L is not a problem, but I is excluded to avoid confusion with 1
// in order to get to 32, we also remove B to avoid confusion with 8
export const BOX_ID_CHARSET = '0123456789ACDEFGHJKLMNPQSTUVWXYZ';
// original 0123456789abcdefghijklmnopqstuvw';

export const MS_PER_S = 1000;
export const MS_PER_MIN = 60 * MS_PER_S;
export const MS_PER_HOUR = 60 * MS_PER_MIN;

export const KM_PER_MILE = 1.609344;

export const ROBOT_IP = '10.0.0.1';

// time constants
export const _MS_PER_SEC = 1000;
export const _MS_PER_MIN = _MS_PER_SEC * 60;
export const _MS_PER_HOUR = _MS_PER_MIN * 60;
export const _MS_PER_DAY = _MS_PER_HOUR * 24;
export const _MS_PER_WEEK = _MS_PER_DAY * 7;

// Approximate meters per degree at mid-latitudes (~40°N).
// Used for cheap bounding-box pre-filters (getCloseCores, etc.).
export const METERS_PER_DEG_LAT = 111_000;
export const METERS_PER_DEG_LON = 85_000; // conservative (smallest at high lat)
export const DEG_PER_METER_LAT = 1 / METERS_PER_DEG_LAT;
export const DEG_PER_METER_LON = 1 / METERS_PER_DEG_LON;

export const M_PER_FT = 0.3048;
export const MM_PER_FT = M_PER_FT * 1000;

export const SAMPLE_SELECTION_TIMEOUT_MS = 2 * _MS_PER_MIN;

export const TARGET_DEPTH_CM_DEFAULT = 30;

export enum MissionState {
  _0_Received_No_Files = '0. Received No Files',
  _1_Received_Partial = '1. Received Partial',
  _2_Ready_2_Make = '2. Ready-2-Make',
  _2X_Mission_Creation_Error = '2X. Mission Creation Error',
  _3_Mission_Auto_Made = '3. Mission Auto-Made',
  _3B_Partially_Made = '3B. Partially Made',
  _4_Mission_Finalized = '4. Mission Finalized',
}

export enum COLOR {
  'GREEN' = 0x00ff00,
  'RED' = 0xff0000,
  'WHITE' = 0xfaf9f6, // soil NOT in bucket, dump NOT required
  'PURPLE' = 0xa020f0,
  'BLUE' = 0x0000ff,
  'YELLOW' = 0xffff00, // dump required
  'ORBRWN' = 0xbe6400, // soil in bucket
  'DARK_YELLOW' = 0x1565c0, // unsampled core for kml layer
  'ORANGE' = 0xffa500,
}

export const SAMPLE_WARNING_DISTANCE_FT = 50;
export const SAMPLE_WARNING_DISTANCE_M = ft_to_m(SAMPLE_WARNING_DISTANCE_FT); // meters
export const CORE_MINIMUM_DISTANCE_RADIUS_M = ft_to_m(15); // meters
export const CORE_DEADBAND_WIDTH_M = ft_to_m(15); // meters

export const DEFAULT_CORE_PRESENT_DISTANCE_FEET = 15;
export const DEFAULT_CORE_PRESENT_DISTANCE_METERS = ft_to_m(DEFAULT_CORE_PRESENT_DISTANCE_FEET); // meters

export const DEFAULT_CORE_CLOSE_DISTANCE_FEET = 50;
export const DEFAULT_CORE_CLOSE_DISTANCE_METERS = ft_to_m(DEFAULT_CORE_CLOSE_DISTANCE_FEET); // meters

export const CORE_MINIMUM_DISTANCE_LEAVING_METERS = ft_to_m(DEFAULT_CORE_PRESENT_DISTANCE_FEET); // meters

export const SAMPLING_TASK = {
  CLOSE_SAMPLE_SELECTION_POPUP: 'CLOSE_SAMPLE_SELECTION_POPUP',
} as const;

export enum FieldParams {
  FieldAlignedSamplePath = 1 << 0,
  NoBoundaryTolerance = 1 << 1,
  ImplementCenteredNavigation = 1 << 2,
}

export const BD_CORE_COMMAND = 3;

export const FERTILITY_CORE_AUTO = 0;
export const FERTILITY_CORE_MANUAL = 1;
export const BD_CORE_MANUAL = 2;
export const BD_CORE_AUTO = 3;

// remaining

export const MINIMUM_CORES = 3;

export function ft_to_m(feet: number) {
  return feet * 0.3048; // 0.3048 meters per foot
}

export const COMPANIES_CUSTOM_POINTS_ID_REGEX = {
  'andes ag':
    'centroid:' +
    '^(\\d+)-([A-Za-z]+)-([A-Za-z0-9]+)-([A-Za-z]+)-(\\d+)-([A-Za-z]+)$:' +
    '^\\d+-[A-Za-z]+-[A-Za-z0-9]+-([A-Za-z]+)-(\\d+)-[A-Za-z]+$' +
    ':$1$2',
};

export const FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB = 3;

export const MINIMUM_COORDINATES_FOR_VALID_BOUNDARY = 4;

export const MIN_LAB_COUNT = 1;

export const MIDPOINT_DUMP_MIN_INCHES = 120;

export const CORE_STOP_MAX_DISTANCE_FT = 2;

export const MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT = 10;

export const PHOTO_UPLOADS_BUCKET_NAME = 'missionphotouploads';

// Smart Path
export const SMART_PATH_GRID_CELL_SIZE_METERS_MIN = 10;
export const SMART_PATH_GRID_CELL_SIZE_METERS_DEFAULT = 50;
export const SMART_PATH_GRID_CELL_SIZE_METERS_MAX = 100;

export const SMART_PATH_PREFERRED_ANGLE_DEGREES_DEFAULT = 90;
export const SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_DEFAULT = 0.75;
export const SMART_PATH_PREFERRED_ANGLE_PENALTY_FACTOR_MAX = 10;

export const SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_MAX = 50;
export const SMART_PATH_BORDER_CLUSTER_PENALTY_FACTOR_DEFAULT = 40;

export const SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_MAX = 30;
export const SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT_FOR_INTERLEAVING = 1;
export const SMART_PATH_ZONE_CLUSTER_PENALTY_FACTOR_DEFAULT = 10;

export const SMART_PATH_TOO_CLOSE_DISTANCE_TO_BOUNDARY_METERS = 6;
export const SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_DEFAULT = 5;
export const SMART_PATH_MIN_DISTANCE_TO_BOUNDARY_METERS_MAX = 10;

// Dubin's time-based cost knobs (mirror pathfinder defaults).
// See pathfinder/docs/lkh-solver.md §"Time-based inter-cluster edge cost".
export const SMART_PATH_CRUISE_SPEED_MPS_DEFAULT = 5.4; // ~12 mph
export const SMART_PATH_CRUISE_SPEED_MPS_MIN = 1.0;
export const SMART_PATH_CRUISE_SPEED_MPS_MAX = 10.0;

export const SMART_PATH_TURN_SPEED_FACTOR_DEFAULT = 0.3; // 70% slowdown on arcs
export const SMART_PATH_TURN_SPEED_FACTOR_MIN = 0.1;
export const SMART_PATH_TURN_SPEED_FACTOR_MAX = 1.0;

export const SMART_PATH_SHARP_TURN_WEIGHT_DEFAULT = 0;
export const SMART_PATH_SHARP_TURN_WEIGHT_MAX = 2;

// DGP iterative-solver outer-loop cap. Pathfinder defaults to 5; raise per
// request when missions have legs that wrap multiple obstacles and the
// solver needs more passes to converge (per-iter cost grows quickly so a
// modest bump is usually enough).
export const SMART_PATH_DGP_ITERATION_CAP_DEFAULT = 5;
export const SMART_PATH_DGP_ITERATION_CAP_MAX = 50;

// Hybrid A* leg solver caps. Pathfinder runs c2c → direct → Hybrid A* →
// corridor (DGP is uncalled since 2026-09-30); these two caps bound how hard
// Hybrid A* tries before falling through to the corridor.
//
// Wall time is the PRIMARY latency cap. The node cap is a memory-safety bound
// (each lattice node ≈ 200 B; 100 k ≈ 20 MB worst case) — well above what real
// missions ever need (a few hundred to low-thousands). Raise the time budget
// only when you see legitimate TIME_BUDGET failures on real missions; raise
// the node cap only if you see legitimate NODE_CAP failures the time budget
// would have solved (rare in practice). 5 s per leg is pathfinder's own
// default (spec 2026-09-30 §5.2).
export const SMART_PATH_HYBRID_ASTAR_NODE_CAP_DEFAULT = 100000;
export const SMART_PATH_HYBRID_ASTAR_NODE_CAP_MAX = 500000;
export const SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_DEFAULT = 5000;
export const SMART_PATH_HYBRID_ASTAR_TIME_BUDGET_MS_MAX = 5000;

export const UNDEFINED_SAMPLE_ID_PREFIX = '_X_ID_';
export const undefinedSampleIdRegex = new RegExp(`^${UNDEFINED_SAMPLE_ID_PREFIX}(\\d+)$`);

export const PERIODIC_DB_SYNC_SAMPLING_SECONDS = 10;
export const PERIODIC_DB_SYNC_NON_SAMPLING_SECONDS = 30;

export const BOUNDARY_MIN_VALID_AREA_SQR_METERS = 5;

export const SIMULATOR_ROBOT_NAME = 'K-SIM';

export const AWS_TESTING_MULTIPUPROSE_BUCKET = 'testing-multipurpose';
